import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';

import {
  flushAttempts,
  flushLessonReads,
  invalidateAnswerCaches,
  invalidateContentCaches,
} from '@/api';
import { rememberRatio } from '@/imageRatio';
import { cloud } from '@/platform';
import { readStored, writeStored } from '@/storage';

export type AsyncState<T> =
  { loading: true } | { loading: false; error: Error } | { loading: false; error: null; data: T };

export class RefreshTick {
  private readonly loads: Promise<unknown>[] = [];
  private tracked: () => void = () => {};
  private readonly started = new Promise<void>((resolve) => {
    this.tracked = resolve;
  });

  track(load: Promise<unknown>): void {
    this.loads.push(load);
    this.tracked();
  }

  async settled(): Promise<void> {
    await Promise.race([this.started, new Promise((resolve) => setTimeout(resolve, 1000))]);
    await Promise.resolve();
    const failed = (await Promise.allSettled(this.loads)).find((r) => r.status === 'rejected');
    if (failed) throw failed.reason;
  }
}

export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
  { keep = false }: { keep?: boolean } = {},
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ loading: true });
  const [prevDeps, setPrevDeps] = useState(deps);

  const depsChanged = deps.some((dep, i) => dep !== prevDeps[i]);
  const hold = depsChanged && keep && !state.loading && state.error === null;
  if (depsChanged) {
    setPrevDeps(deps);
    if (!hold) setState({ loading: true });
  }

  useEffect(() => {
    let cancelled = false;
    const load = loader();
    deps.find((dep): dep is RefreshTick => dep instanceof RefreshTick)?.track(load);
    load
      .then((data) => !cancelled && setState({ loading: false, error: null, data }))
      .catch(
        (e: unknown) =>
          !cancelled &&
          setState((prev) =>
            keep && !prev.loading && prev.error === null
              ? prev
              : { loading: false, error: e instanceof Error ? e : new Error(String(e)) },
          ),
      );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return depsChanged && !hold ? { loading: true } : state;
}

export function dataOf<T>(state: AsyncState<T>): T | null {
  return state.loading || state.error !== null ? null : state.data;
}

let reducedMotion: MediaQueryList | null | undefined;

export function prefersReducedMotion(): boolean {
  if (reducedMotion === undefined) {
    reducedMotion =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
  }
  return reducedMotion?.matches ?? false;
}

export function useRefreshable(): [RefreshTick, () => Promise<void>] {
  const [tick, setTick] = useState(() => new RefreshTick());
  const refresh = useCallback(async () => {
    invalidateAnswerCaches();
    invalidateContentCaches();
    const next = new RefreshTick();
    setTick(next);
    await next.settled();
  }, []);
  return [tick, refresh];
}

export function CountUp({ to }: { to: number }): number {
  return useCountUp(to);
}

export function useCountUp(target: number): number {
  const animate = target > 0 && !prefersReducedMotion();
  const [shown, setShown] = useState(animate ? 0 : target);
  useEffect(() => {
    if (!animate) return;
    const id = setInterval(
      () => {
        setShown((s) => {
          if (s + 1 >= target) clearInterval(id);
          return Math.min(target, s + 1);
        });
      },
      Math.max(28, Math.round(650 / target)),
    );
    return () => clearInterval(id);
  }, [animate, target]);
  return animate ? shown : target;
}

export function useFlushAttempts(): void {
  useEffect(() => {
    const flush = () => {
      void flushAttempts().catch(() => {});
      void flushLessonReads().catch(() => {});
    };
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, []);
}

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

export function useAlive(): { readonly current: boolean } {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
}

export function usePrefetchImage(url: string | null): void {
  useEffect(() => {
    if (!url) return;
    const net = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (net?.saveData || net?.effectiveType?.includes('2g')) return;
    const img = new Image();
    img.onload = () => rememberRatio(url, img.naturalWidth, img.naturalHeight);
    img.src = url;
  }, [url]);
}

export function useCloudSynced<T>(
  key: string,
  parse: (raw: unknown) => T | null,
  fallback: T,
  serialize: (value: T) => string = String,
): [T, (action: SetStateAction<T>) => void] {
  const [value, setValue] = useState<T>(() => parse(readStored(key)) ?? fallback);
  const touched = useRef(false);
  const current = useRef(value);

  useEffect(() => {
    if (!cloud.canRead()) return;
    void cloud
      .read(key)
      .then((raw) => {
        const synced = parse(raw);
        if (touched.current || synced === null) return;
        writeStored(key, serialize(synced));
        current.current = synced;
        setValue(synced);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback(
    (action: SetStateAction<T>) => {
      touched.current = true;
      const next = action instanceof Function ? action(current.current) : action;
      current.current = next;
      setValue(next);
      const raw = serialize(next);
      writeStored(key, raw);
      cloud.write(key, raw);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [value, set];
}
